import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Star, ArrowRight, CalendarBlank, MapPin } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { disciplineLabel } from "../lib/disciplines";

const initials = (name) =>
  (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join("");

// Deterministic accent for avatar based on the name
const accentFor = (name) => {
  const palette = ["#E30613", "#0055A4", "#DA291C", "#8B1E3F", "#2E5D34", "#B8860B"];
  const hash = Array.from(name || "").reduce((h, c) => h + c.charCodeAt(0), 0);
  return palette[hash % palette.length];
};

const bioExcerpt = (bio, max = 110) => {
  if (!bio) return "";
  const trimmed = bio.trim();
  if (trimmed.length <= max) return trimmed;
  return trimmed.slice(0, max).trimEnd() + "…";
};

const formatDate = (iso, lang) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === "de" ? "de-CH" : lang === "fr" ? "fr-CH" : "it-CH", {
      day: "2-digit",
      month: "short",
    });
  } catch {
    return "";
  }
};

const formatTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

function CoachCard({ coach, upcomingCourse, lang, t }) {
  const rating = coach.rating_avg || 0;
  const count = coach.rating_count || 0;
  const accent = accentFor(coach.name);
  const bio = bioExcerpt(coach.bio);

  return (
    <Link
      to={`/instructors/${coach.id}`}
      data-testid={`featured-coach-${coach.id}`}
      className="group relative flex flex-col border border-white/10 bg-[#0A0A0A] hover:border-[#E30613]/60 hover:-translate-y-0.5 transition-all duration-200 overflow-hidden"
    >
      {/* Header: avatar + name */}
      <div className="p-6 border-b border-white/5">
        <div className="flex items-start gap-4">
          {coach.avatar_url ? (
            <img
              src={coach.avatar_url}
              alt={coach.name}
              loading="lazy"
              className="w-14 h-14 shrink-0 object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
            />
          ) : null}
          <div
            className={`w-14 h-14 shrink-0 items-center justify-center font-display text-xl font-black text-white ${coach.avatar_url ? "hidden" : "flex"}`}
            style={{ backgroundColor: accent }}
            aria-hidden="true"
          >
            {initials(coach.name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div className="font-display text-lg uppercase font-black text-white leading-tight truncate">
                {coach.name}
              </div>
              {coach.is_demo && (
                <span className="shrink-0 text-[9px] uppercase tracking-[0.2em] font-bold bg-amber-500 text-black px-1.5 py-0.5" title={t("demo.badge_full")}>
                  {t("demo.badge_short")}
                </span>
              )}
            </div>
            <div className="mt-1.5 flex items-center gap-1.5">
              {count > 0 ? (
                <>
                  <Star size={14} weight="fill" className="text-[#E30613]" />
                  <span className="text-sm font-bold text-white">{rating.toFixed(1)}</span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    · {count} {count === 1 ? t("featured.review_singular") : t("featured.reviews")}
                  </span>
                </>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold border border-[#E30613]/40 px-2 py-0.5">
                  <Star size={9} weight="fill" /> {t("featured.no_rating")}
                </span>
              )}
            </div>
          </div>
        </div>

        {bio && (
          <p className="mt-4 text-xs text-zinc-400 leading-relaxed line-clamp-2">{bio}</p>
        )}
      </div>

      {/* Next course */}
      <div className="p-6 flex-1 flex flex-col justify-between bg-black/40">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold">
            {t("featured.next_course")}
          </div>
          {upcomingCourse ? (
            <div className="mt-3">
              <div className="text-sm font-bold text-white leading-tight">
                {upcomingCourse.title}
              </div>
              <div className="mt-1 text-[11px] uppercase tracking-wider text-zinc-500">
                {disciplineLabel(upcomingCourse.discipline, lang)}
              </div>
              <div className="mt-3 flex items-center gap-3 text-[11px] text-zinc-400">
                <span className="inline-flex items-center gap-1">
                  <CalendarBlank size={12} weight="duotone" className="text-[#E30613]" />
                  {formatDate(upcomingCourse.starts_at, lang)} · {formatTime(upcomingCourse.starts_at)}
                </span>
                {upcomingCourse.location && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin size={12} weight="duotone" className="text-[#E30613]" />
                    {upcomingCourse.location}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-3 text-xs text-zinc-500 italic">{t("featured.no_upcoming")}</div>
          )}
        </div>

        <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 group-hover:text-white font-bold transition-colors">
            {t("featured.view_profile")}
          </span>
          <ArrowRight size={14} className="text-zinc-500 group-hover:text-[#E30613] group-hover:translate-x-1 transition-all" />
        </div>
      </div>
    </Link>
  );
}

export default function FeaturedCoaches({ courses = [] }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || "it";
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/instructors");
      setCoaches(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Sort: those with reviews first (by avg desc, then count desc), then newer coaches.
  const top = useMemo(() => {
    const sorted = [...coaches].sort((a, b) => {
      const aHas = (a.rating_count || 0) > 0;
      const bHas = (b.rating_count || 0) > 0;
      if (aHas !== bHas) return bHas - aHas;
      if (aHas && bHas) {
        const dr = (b.rating_avg || 0) - (a.rating_avg || 0);
        if (dr !== 0) return dr;
        return (b.rating_count || 0) - (a.rating_count || 0);
      }
      return (a.name || "").localeCompare(b.name || "");
    });
    return sorted.slice(0, 6);
  }, [coaches]);

  // Map instructor_id → next upcoming course from the already-loaded landing courses
  const nextCourseByInstructor = useMemo(() => {
    const now = Date.now();
    const map = {};
    for (const c of courses) {
      if (!c.starts_at) continue;
      const ts = new Date(c.starts_at).getTime();
      if (Number.isNaN(ts) || ts < now) continue;
      const cur = map[c.instructor_id];
      if (!cur || new Date(cur.starts_at).getTime() > ts) map[c.instructor_id] = c;
    }
    return map;
  }, [courses]);

  if (loading) return null;
  if (top.length === 0) return null;

  return (
    <section className="border-b border-white/10 bg-[#050505]" data-testid="featured-coaches">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div className="max-w-2xl">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold">{t("featured.tag")}</div>
            <h2 className="font-display text-4xl sm:text-5xl uppercase font-black text-white mt-2 tracking-tight leading-[0.95]">
              {t("featured.title")}
            </h2>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed">{t("featured.subtitle")}</p>
          </div>
          <Link
            to="/instructors"
            data-testid="featured-coaches-view-all"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white hover:text-[#E30613] font-bold transition-colors border border-white/20 hover:border-[#E30613] px-4 py-3"
          >
            {t("featured.view_all")} <ArrowRight size={12} weight="bold" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {top.map((c) => (
            <CoachCard
              key={c.id}
              coach={c}
              upcomingCourse={nextCourseByInstructor[c.id]}
              lang={lang}
              t={t}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
